/**
 * Order Medication Service
 * Comprehensive service for managing medication orders with all scenarios
 */

import apiClient from './apiClient';
import {
    MedicationOrderDTO,
    CreateMedicationOrderRequest,
    MedicationOrderStatus,
    PageResponse,
    ReceiveMedicationOrderRequest,
} from '../types/medication.types';

const BASE_PATH = '/v1/medication-orders';

/**
 * Interface for medication order filters
 */
export interface MedicationOrderFilters {
    residentId?: string;
    status?: MedicationOrderStatus;
    startDate?: string;
    endDate?: string;
    medicationName?: string;
    prescribingPhysician?: string;
    facilityId?: string;
}

/**
 * Interface for bulk operations
 */
export interface BulkOperationResult {
    successCount: number;
    failureCount: number;
    errors: Array<{ id: string; error: string }>;
}

/**
 * Interface for medication order statistics
 */
export interface MedicationOrderStats {
    totalOrders: number;
    activeOrders: number;
    completedOrders: number;
    cancelledOrders: number;
    onHoldOrders: number;
    expiringOrders: number;
}

class OrderMedicationService {
    // ============================================
    // CREATE OPERATIONS
    // ============================================

    /**
     * Create a new medication order with one or more medications
     */
    async createOrder(request: FormData | CreateMedicationOrderRequest): Promise<MedicationOrderDTO> {
        try {
            const config = request instanceof FormData
                ? { headers: { 'Content-Type': 'multipart/form-data' } }
                : undefined;
            const response = await apiClient.post<MedicationOrderDTO>(BASE_PATH, request, config);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to create medication order');
        }
    }

    /**
     * Duplicate an existing medication order
     */
    async duplicateOrder(orderId: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${orderId}/duplicate`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to duplicate medication order');
        }
    }

    // ============================================
    // READ OPERATIONS
    // ============================================

    /**
     * Get medication order by ID
     */
    async getOrderById(id: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.get<MedicationOrderDTO>(`${BASE_PATH}/${id}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order');
        }
    }

    /**
     * Get medication order by order number
     */
    async getOrderByOrderNumber(orderNumber: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.get<MedicationOrderDTO>(`${BASE_PATH}/order-number/${orderNumber}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order by order number');
        }
    }

    /**
     * Get all medication orders with pagination
     */
    async getAllOrders(page: number = 0, size: number = 20): Promise<PageResponse<MedicationOrderDTO>> {
        try {
            const response = await apiClient.get<PageResponse<MedicationOrderDTO>>(BASE_PATH, {
                params: { page, size },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication orders');
        }
    }

    /**
     * Get medication orders with advanced filtering
     */
    async getFilteredOrders(
        filters: MedicationOrderFilters,
        page: number = 0,
        size: number = 20
    ): Promise<PageResponse<MedicationOrderDTO>> {
        try {
            const response = await apiClient.get<PageResponse<MedicationOrderDTO>>(BASE_PATH, {
                params: { ...filters, page, size },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch filtered medication orders');
        }
    }

    /**
     * Get all medication orders for a specific resident
     */
    async getOrdersByResident(residentId: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/resident/${residentId}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch resident medication orders');
        }
    }

    /**
     * Get active medication orders for a specific resident
     */
    async getActiveOrdersByResident(residentId: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/resident/${residentId}/active`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch active medication orders');
        }
    }

    /**
     * Get medication orders by status
     */
    async getOrdersByStatus(status: MedicationOrderStatus): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/status/${status}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication orders by status');
        }
    }

    /**
     * Get medication orders by facility
     */
    async getOrdersByFacility(facilityId: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/facility/${facilityId}?size=2000`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch facility medication orders');
        }
    }

    /**
     * Get medication order count by facility
     */
    async getMedicationOrderCount(facilityId: string): Promise<number> {
        try {
            const response = await apiClient.get<number>(`${BASE_PATH}/facility/${facilityId}/count`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order count');
        }
    }

    /**
     * Get medication order status counts by facility
     */
    async getMedicationOrderStatusCount(facilityId: string): Promise<{ [key: string]: number }> {
        try {
            const response = await apiClient.get<{ [key: string]: number }>(`${BASE_PATH}/status-count`, {
                params: { facilityId }
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order status counts');
        }
    }

    /**
     * Get expiring medication orders for a specific facility
     */
    async getExpiringOrdersByFacility(facilityId: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<any>(`${BASE_PATH}/expiring/${facilityId}`);

            // Handle if response is a map (key -> array) - Seen in actual API response
            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                const flattenedOrders: any[] = [];
                Object.entries(response.data).forEach(([key, items]) => {
                    if (Array.isArray(items)) {
                        items.forEach((item: any) => {
                            // Inject the key as residentId if it's not already there
                            flattenedOrders.push({ ...item, residentId: item.residentId || key });
                        });
                    }
                });
                return flattenedOrders as MedicationOrderDTO[];
            }

            // Handle if response is directly an array
            if (Array.isArray(response.data)) {
                return response.data;
            }
            return [];
        } catch (error: any) {
            console.error('Failed to fetch expiring medication orders by facility', error);
            return [];
        }
    }

    /**
     * Get medication order statistics
     */
    async getOrderStatistics(facilityId?: string): Promise<MedicationOrderStats> {
        try {
            const response = await apiClient.get<MedicationOrderStats>(`${BASE_PATH}/statistics`, {
                params: facilityId ? { facilityId } : {},
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order statistics');
        }
    }

    /**
     * Search medication orders by medication name
     */
    async searchByMedicationName(medicationName: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/search`, {
                params: { medicationName },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search medication orders');
        }
    }

    /**
     * Search medication orders with multiple filters
     * Supports searching by resident name, physician name, medication name, and status
     */
    async searchOrders(params: {
        facilityId: string;
        residentName?: string;
        physicianName?: string;
        medicationName?: string;
        status?: MedicationOrderStatus;
        pharmacyName?: string;
        pharmacyEmail?: string;
        searchTerm?: string;
    }): Promise<MedicationOrderDTO[]> {
        try {
            const queryParams: Record<string, string> = {
                facilityId: params.facilityId,
                size: '2000',
            };

            if (params.residentName) {
                queryParams.residentName = params.residentName;
            }
            if (params.physicianName) {
                queryParams.physicianName = params.physicianName;
            }
            if (params.medicationName) {
                queryParams.medicationName = params.medicationName;
            }
            if (params.status) {
                queryParams.status = params.status;
            }
            if (params.pharmacyName) {
                queryParams.pharmacyName = params.pharmacyName;
            }
            if (params.pharmacyEmail) {
                queryParams.pharmacyEmail = params.pharmacyEmail;
            }
            if (params.searchTerm) {
                queryParams.searchTerm = params.searchTerm;
            }

            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/search-order`, {
                params: queryParams,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search medication orders');
        }
    }

    // ============================================
    // UPDATE OPERATIONS
    // ============================================

    /**
     * Get medication stock by resident ID
     */
    async getMedicationStockByResident(residentId: string): Promise<any[]> {
        try {
            const response = await apiClient.get<any[]>(`/v1/medication-stock/resident/${residentId}`);
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch medication stock', error);
            return [];
        }
    }

    /**
     * Bulk add medication stock
     */
    async bulkAddStock(request: { residentId: string; adjustments: { medicationId: string; quantity: number }[] }): Promise<any> {
        try {
            const response = await apiClient.post(`/v1/medication-stock/bulk-add`, request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to bulk add medication stock');
        }
    }

    /**
     * Update medication order
     */
    async updateOrder(id: string, data: Partial<MedicationOrderDTO>): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.put<MedicationOrderDTO>(`${BASE_PATH}/${id}`, data);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update medication order');
        }
    }

    /**
     * Update medication order status
     */
    async updateOrderStatus(id: string, status: MedicationOrderStatus): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.patch<MedicationOrderDTO>(`${BASE_PATH}/${id}/status`, null, {
                params: { status },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update medication order status');
        }
    }

    /**
     * Approve medication order
     */
    async approveOrder(id: string, approverNotes?: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/approve`, {
                approverNotes,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to approve medication order');
        }
    }

    /**
     * Reject medication order
     */
    async rejectOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/reject`, null, {
                params: { reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to reject medication order');
        }
    }

    /**
     * Discontinue medication order
     */
    async discontinueOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/discontinue`, null, {
                params: { reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to discontinue medication order');
        }
    }

    /**
     * Put medication order on hold
     */
    async holdOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/hold`, null, {
                params: { reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to put medication order on hold');
        }
    }

    /**
     * Resume medication order from hold
     */
    async resumeOrder(id: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/resume`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to resume medication order');
        }
    }

    /**
     * Renew medication order
     */
    async renewOrder(id: string, newEndDate?: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/renew`, null, {
                params: newEndDate ? { newEndDate } : {},
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to renew medication order');
        }
    }

    /**
     * Modify dosage for medication order
     */
    async modifyDosage(id: string, newDosage: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.patch<MedicationOrderDTO>(`${BASE_PATH}/${id}/dosage`, null, {
                params: { newDosage, reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to modify medication dosage');
        }
    }

    /**
     * Modify frequency for medication order
     */
    async modifyFrequency(id: string, newFrequency: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.patch<MedicationOrderDTO>(`${BASE_PATH}/${id}/frequency`, null, {
                params: { newFrequency, reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to modify medication frequency');
        }
    }

    // ============================================
    // DELETE OPERATIONS
    // ============================================

    /**
     * Delete medication order
     */
    async deleteOrder(id: string): Promise<void> {
        try {
            await apiClient.delete(`${BASE_PATH}/${id}`);
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete medication order');
        }
    }

    /**
     * Delete multiple medication orders
     */
    async deleteBulkOrders(ids: string[]): Promise<BulkOperationResult> {
        try {
            const response = await apiClient.post<BulkOperationResult>(`${BASE_PATH}/bulk-delete`, { ids });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete bulk medication orders');
        }
    }

    // ============================================
    // REPORTING & EXPORT OPERATIONS
    // ============================================

    /**
     * Export medication orders to CSV
     */
    async exportToCSV(filters?: MedicationOrderFilters): Promise<Blob> {
        try {
            const response = await apiClient.get(`${BASE_PATH}/export/csv`, {
                params: filters,
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to export medication orders to CSV');
        }
    }

    /**
     * Export medication orders to PDF
     */
    async exportToPDF(filters?: MedicationOrderFilters): Promise<Blob> {
        try {
            const response = await apiClient.get(`${BASE_PATH}/export/pdf`, {
                params: filters,
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to export medication orders to PDF');
        }
    }

    /**
     * Get medication order PDF
     */
    async getOrderPdf(id: string, signal?: AbortSignal): Promise<Blob> {
        try {
            const response = await apiClient.get<ArrayBuffer>(`${BASE_PATH}/${id}/pdf`, {
                responseType: 'arraybuffer',
                signal
            });
            const mimeType = response.headers['content-type'] || 'application/pdf';
            return new Blob([response.data], { type: mimeType });
        } catch (error: any) {
            if (error.name === 'AbortError') throw error;
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order PDF');
        }
    }

    async generateMAR(residentId: string, startDate?: string, endDate?: string): Promise<Blob> {
        try {
            const params: Record<string, string> = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await apiClient.get(`${BASE_PATH}/generate-pdf/${residentId}`, {
                params,
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to generate MAR');
        }
    }

    // ============================================
    // VALIDATION & VERIFICATION OPERATIONS
    // ============================================

    /**
     * Validate medication order before creation
     */
    async validateOrder(request: CreateMedicationOrderRequest): Promise<{ valid: boolean; errors: string[] }> {
        try {
            const response = await apiClient.post<{ valid: boolean; errors: string[] }>(`${BASE_PATH}/validate`, request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to validate medication order');
        }
    }

    /**
     * Check for drug interactions
     */
    async checkDrugInteractions(residentId: string, medicationName: string): Promise<{ hasInteractions: boolean; interactions: string[] }> {
        try {
            const response = await apiClient.get<{ hasInteractions: boolean; interactions: string[] }>(`${BASE_PATH}/interactions`, {
                params: { residentId, medicationName },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check drug interactions');
        }
    }

    /**
     * Check for duplicate orders
     */
    async checkDuplicates(residentId: string, medicationName: string): Promise<{ hasDuplicates: boolean; duplicates: MedicationOrderDTO[] }> {
        try {
            const response = await apiClient.get<{ hasDuplicates: boolean; duplicates: MedicationOrderDTO[] }>(`${BASE_PATH}/duplicates`, {
                params: { residentId, medicationName },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check for duplicate orders');
        }
    }

    // ============================================
    // NOTIFICATION & ALERT OPERATIONS
    // ============================================

    /**
     * Send notification for medication order
     */
    async sendNotification(id: string, recipientIds: string[], message: string): Promise<void> {
        try {
            await apiClient.post(`${BASE_PATH}/${id}/notify`, {
                recipientIds,
                message,
            });
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to send notification');
        }
    }

    /**
     * Send medication order to pharmacy with signature
     */
    async sendToPharmacy(id: string, signatureBase64: string): Promise<void> {
        try {
            // Extract just the base64 part if it's a data URL
            let base64Data = signatureBase64;
            if (signatureBase64.includes(',')) {
                base64Data = signatureBase64.split(',')[1];
            }

            await apiClient.post(`${BASE_PATH}/${id}/send-to-pharmacy`, {
                signatureBase64: base64Data
            });
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to send to pharmacy');
        }
    }

    /**
     * Get Monthly MAR records for a resident
     */
    async getMonthlyMAR(facilityId: string, residentId: string, startDate: string, endDate: string, medicationName?: string): Promise<any> {
        try {
            const params: any = {
                residentId,
                startDate,
                endDate,
            };
            if (medicationName) {
                params.medicationName = medicationName;
            }

            const response = await apiClient.get<any>('/v1/medication-status/monthly', { params });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch Monthly MAR data');
        }
    }

    /**
     * Get Daily MAR records for a resident
     */
    async getDailyMAR(residentId: string, date: string): Promise<any> {
        try {
            const response = await apiClient.get<any>(`/v1/medication-status/daily`, {
                params: { residentId, date }
            });
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch Daily MAR data', error);
            return [];
        }
    }

    /**
     * Get Weekly MAR records for a resident
     */
    async getWeeklyMAR(residentId: string, startDate: string, endDate: string): Promise<any> {
        try {
            const response = await apiClient.get<any>(`/v1/medication-status/weekly`, {
                params: { residentId, startDate, endDate }
            });
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch Weekly MAR data', error);
            return [];
        }
    }

    /**
     * Get medications needing refill soon (fetches all items across pagination)
     */
    async getRefillEndingSoon(facilityId?: string, size = 1000): Promise<any[]> {
        try {
            if (!facilityId) return [];

            const extractItems = (data: any): any[] => {
                if (!data) return [];
                if (Array.isArray(data)) return data;
                if (typeof data === 'object') {
                    if (Array.isArray(data.content)) return data.content;
                    if (Array.isArray(data.data)) return data.data;
                    if (data.data && Array.isArray(data.data.content)) return data.data.content;
                    if (Array.isArray(data.items)) return data.items;
                    if (Array.isArray(data.medications)) return data.medications;
                    if (Array.isArray(data.records)) return data.records;
                    if (Array.isArray(data.result)) return data.result;

                    // Handle Map (key -> array of medications, e.g. Map<residentId, List<EMRMedication>>)
                    const flattenedOrders: any[] = [];
                    Object.entries(data).forEach(([key, items]) => {
                        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
                        if (Array.isArray(items)) {
                            items.forEach((item: any) => {
                                if (item && typeof item === 'object') {
                                    if (Array.isArray(item.medicationItems) && item.medicationItems.length > 0) {
                                        item.medicationItems.forEach((subItem: any) => {
                                            flattenedOrders.push({
                                                ...subItem,
                                                ...item,
                                                medicationName: subItem.medicationName || item.medicationName,
                                                dosage: subItem.dosage || item.dosage,
                                                endDate: subItem.endDate || item.endDate,
                                                residentId: item.residentId || item.resident_id || (isUuid ? key : undefined),
                                                residentName: item.residentName || item.residentFullName || item.resident_name || (!isUuid ? key : undefined),
                                                groupedKey: key,
                                            });
                                        });
                                    } else {
                                        flattenedOrders.push({
                                            ...item,
                                            residentId: item.residentId || item.resident_id || (isUuid ? key : undefined),
                                            residentName: item.residentName || item.residentFullName || item.resident_name || (!isUuid ? key : undefined),
                                            groupedKey: key,
                                        });
                                    }
                                }
                            });
                        } else if (items && typeof items === 'object') {
                            flattenedOrders.push({
                                ...(items as any),
                                residentId: (items as any).residentId || (items as any).resident_id || (isUuid ? key : undefined),
                                residentName: (items as any).residentName || (items as any).residentFullName || (!isUuid ? key : undefined),
                                groupedKey: key,
                            });
                        }
                    });

                    if (flattenedOrders.length > 0) {
                        return flattenedOrders;
                    }

                    if (data.route || data.medicationName || data.schedule || data.startDate) {
                        return [data];
                    }
                }
                return [];
            };

            const response = await apiClient.get<any>(`/v1/EMR-medications/facility/${facilityId}/refill-ending-soon`, {
                params: { page: 0, size }
            });

            let allItems = extractItems(response.data);

            // If the backend enforces smaller page size (e.g. 10 or 20) and returned totalPages > 1
            const totalPages = response.data?.totalPages || response.data?.data?.totalPages || response.data?.page?.totalPages;
            if (typeof totalPages === 'number' && totalPages > 1) {
                const pagePromises: Promise<any>[] = [];
                for (let p = 1; p < totalPages; p++) {
                    pagePromises.push(
                        apiClient.get<any>(`/v1/EMR-medications/facility/${facilityId}/refill-ending-soon`, {
                            params: { page: p, size }
                        }).then(res => extractItems(res.data)).catch(() => [])
                    );
                }
                const pagesData = await Promise.all(pagePromises);
                pagesData.forEach(pageItems => {
                    if (Array.isArray(pageItems) && pageItems.length > 0) {
                        allItems = allItems.concat(pageItems);
                    }
                });
            }

            return allItems;
        } catch (error: any) {
            console.error('Failed to fetch refill ending soon list', error);
            return [];
        }
    }

    /**
     * Get medication orders ending soon for a facility
     */
    async getMedicationOrdersEnding(facilityId: string): Promise<any[]> {
        try {
            const response = await apiClient.get<any>(`${BASE_PATH}/ending/${facilityId}`);

            // Handle if response is a map (key -> array)
            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                const flattenedOrders: any[] = [];
                Object.entries(response.data).forEach(([key, items]) => {
                    if (Array.isArray(items)) {
                        items.forEach((item: any) => {
                            // Inject the key as residentId if it's not already there
                            flattenedOrders.push({ ...item, residentId: item.residentId || key });
                        });
                    }
                });
                return flattenedOrders;
            }

            // Handle if response is directly an array
            if (Array.isArray(response.data)) {
                return response.data;
            }
            return [];
        } catch (error: any) {
            console.error('Failed to fetch medication orders ending', error);
            return [];
        }
    }

    /**
     * Get medication dose summary statistics for count tiles
     */
    async getDoseSummary(residentId: string, startDate: string, endDate: string): Promise<any> {
        try {
            const response = await apiClient.get<any>('/v1/medication-status/medication/dose-summary', {
                params: { residentId, startDate, endDate }
            });
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch dose summary data', error);
            return null;
        }
    }

    /**
     * Receive medication order items (full or partial delivery)
     */
    async receiveOrder(orderId: string, request: ReceiveMedicationOrderRequest): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${orderId}/receive`, request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to receive medication order stock');
        }
    }
}

export default new OrderMedicationService();

